import { useCallback, useMemo } from 'react';
import { Checkbox, IconButton } from '@mui/material';
import {
  DataGridPremium,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
} from '@mui/x-data-grid-premium';
import { StackedTextCell } from '@retail/components';
import { DraftCondition } from '@retail/calculus/types';
import { useAppTFunction } from '@retail/app/i18n';
import { getDataGridLocalization, getLLDate, MgPrisLocale } from '@retail/utils';
import { useRowColorGrouping } from '@retail/hooks';
import { TrashIcon } from '@sanity/icons';
import { ConditionTypeChip } from './ConditionTypeChip';
import { ChangeType } from './ChangeType';
import { LevelChip } from './LevelChip';
import { dataGridNoHorizontalPaddingClass, dataGridWithCellBordersClass } from '@shared/styles';
import { OrgUnitAssortment } from '@retail/mgpris/config-types';
import { getConditionFromType } from '@retail/calculus/utils';

interface Props {
  conditions: DraftCondition[];
  priceLists: OrgUnitAssortment[];
  isLoading: boolean;
  language: string;
  checkedConditionIdsForSupplier: number[];
  salesPriceConditionsIsEnabled: boolean;
  handleSelectConditionId: (conditionId: number) => void;
  handleDeleteCondition: (conditionId: number) => void;
}

type GridRowCondition = GridColDef<DraftCondition>;

const baseColumnProps: Partial<GridRowCondition> = {
  width: 150,
  flex: 1,
  headerAlign: 'left',
  align: 'left',
};

export const DraftTableContent = ({
  conditions,
  priceLists,
  isLoading,
  language,
  checkedConditionIdsForSupplier,
  salesPriceConditionsIsEnabled,
  handleSelectConditionId,
  handleDeleteCondition,
}: Props) => {
  const t = useAppTFunction();
  const { getRowColorByIdGroups } = useRowColorGrouping();

  const isSelectedRow = useCallback(
    (row: DraftCondition) => !!checkedConditionIdsForSupplier?.includes(row.condition.id),
    [checkedConditionIdsForSupplier]
  );

  const rowIds = useMemo(() => conditions.map((cond) => cond.condition.id), [conditions]);

  const columns: GridRowCondition[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        field: 'checkbox',
        align: 'right',
        width: 80,
        renderCell: ({ row }) => {
          return (
            <Checkbox
              checked={isSelectedRow(row)}
              onClick={() => handleSelectConditionId(row.condition.id)}
            />
          );
        },
      },

      {
        ...baseColumnProps,
        field: 'type',
        maxWidth: 180,
        minWidth: 160,
        align: 'center',
        headerName: t('purchaseAgreements.agreement.columns.type'),
        renderCell: ({ row }) => {
          const customerMarkupsPriceListOrigin = priceLists?.find(
            (priceList) => priceList.draftAssortmentId === row.condition.assortmentId
          );
          return (
            <ConditionTypeChip
              calculusArea={row.condition.type}
              height={customerMarkupsPriceListOrigin && 50}
              labelDetails={customerMarkupsPriceListOrigin?.name}
              salesPriceConditionsIsEnabled={salesPriceConditionsIsEnabled}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'typeOfAction',
        headerName: t('calculus.typeOfAction'),
        maxWidth: 120,
        renderCell: ({ row }) => {
          const condition = getConditionFromType(row.condition);
          const conditionReplaces = row.replacesCondition
            ? getConditionFromType(row.replacesCondition)
            : undefined;
          if (condition?.isDeletionCondition) return t('calculus.removeCondition');
          else if (!conditionReplaces) return t('calculus.newCondition');
          else if (conditionReplaces) return t('calculus.replacesCondition');

          return '-';
        },
      },
      {
        ...baseColumnProps,
        field: 'level',
        headerName: t('calculus.level.changeLevel'),
        maxWidth: 170,
        minWidth: 160,
        align: 'center',
        renderCell: ({ row }) => {
          return (
            <LevelChip
              bindingType={row.condition.bindingType}
              bindingTargetItem={row.condition.bindingTargetItem}
              active
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'itemOrLevelName',
        headerName: t('purchaseAgreements.agreement.columns.levelOrItemName'),
        renderCell: ({ row }) => {
          const prevLevelName = row.replacesCondition?.bindingTargetName;
          const newLevelName = row.condition.bindingTargetName;
          if (!!prevLevelName && prevLevelName !== newLevelName) {
            return (
              <StackedTextCell
                topLine={newLevelName ?? '-'}
                bottomLine={prevLevelName}
                nullableBottomLine
                slotProps={{ bottomLine: { lineThrough: true } }}
              />
            );
          }
          return newLevelName ?? '-';
        },
      },

      {
        ...baseColumnProps,
        field: 'changeType',
        headerName: t('calculus.changeType'),
        cellClassName: dataGridNoHorizontalPaddingClass,
        renderCell: ({ row }) => <ChangeType condition={row} conditions={conditions} />,
      },
      {
        ...baseColumnProps,
        field: 'validFromTo',
        headerName: t('calculus.activeFromTo'),
        maxWidth: 150,
        renderCell: ({ row }) => {
          const conditionType = row.condition.type;
          const condition =
            conditionType === 'PURCHASE'
              ? row.condition.purchaseCondition
              : conditionType === 'FREIGHT'
              ? row.condition.freightCondition
              : conditionType === 'MARKUP'
              ? row.condition.markupCondition
              : conditionType === 'PRICE_SOURCE'
              ? row.condition.priceSourceCondition
              : conditionType === 'ROUNDING'
              ? row.condition.roundingCondition
              : row.condition.customerMarkupCondition;
          return (
            <StackedTextCell
              topLine={getLLDate(condition?.validFrom) ?? '-'}
              bottomLine={getLLDate(condition?.validTo)}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        align: 'center',
        maxWidth: 50,
        width: 50,
        renderCell: ({ row }) => {
          return (
            <IconButton
              disabled={isLoading}
              onClick={() => handleDeleteCondition(row.condition.id)}
            >
              <TrashIcon fontSize="25px" />
            </IconButton>
          );
        },
      },
    ],
    [
      t,
      isSelectedRow,
      handleSelectConditionId,
      priceLists,
      salesPriceConditionsIsEnabled,
      conditions,
      isLoading,
      handleDeleteCondition,
    ]
  );

  return (
    <DataGridPremium<DraftCondition>
      getRowId={(row) => row.condition.id}
      columns={columns}
      rows={conditions}
      getRowHeight={() => 'auto'}
      getRowClassName={(value) =>
        getRowColorByIdGroups(rowIds, value.row.condition.id, value.indexRelativeToCurrentPage)
      }
      className={dataGridWithCellBordersClass}
      disableRowSelectionOnClick
      disableColumnMenu
      disableColumnResize
      disableColumnReorder
      hideFooter
      localeText={getDataGridLocalization(language as MgPrisLocale)}
      sx={{ '& .MuiDataGrid-cell': { py: '16px !important' } }}
    />
  );
};
